<template>
  <Teleport to="body">
    <!-- Every way out of a handshake is a deliberate one, so tapping away is the
         only close there is: no cross to aim at over the board. -->
    <SimpleDialog
      class="handshake-sheet"
      :class="{
        'is-dimmed': dimmed,
        'has-toolbar': !!slots.toolbar,
        'has-step': !!slots.step,
        'has-step-shown': stepShown,
      }"
      hide-back
      @close="emit('close')"
    >
      <img v-if="photo" class="sheet-photo" :class="{ 'is-lifted': photoLifted }" :src="photo" alt="" />
      <div v-else class="sheet-board" aria-hidden="true" />

      <!--
        Opening a dialog puts the focus somewhere: absent something named, it
        goes to the first thing inside that can hold it, which on a sheet whose
        heading is plain text is the note line — a caret in the field and the
        keyboard up over the board before anyone has asked for it. Naming the
        board itself keeps it on something that does not type, and keeps it the
        same on every sheet rather than depending on what happens to stand
        highest. Not reachable by tab, so nothing changes for the keyboard.
      -->
      <div class="sheet-content" tabindex="-1" autofocus :class="contentClass">
        <slot v-if="cover" name="cover" />
        <template v-else>
          <div class="sheet-top"><slot name="top" /></div>

          <div class="sheet-middle"><slot /></div>

          <div class="sheet-bottom"><slot name="bottom" /></div>
        </template>
      </div>

      <!-- The left corner holds the photo itself, which is what gets held down
           to lift it, so it is the one thing that stays put while it is. -->
      <div class="sheet-corner is-top-left"><slot name="top-left" /></div>

      <div class="sheet-corner is-top-right"><slot name="top-right" /></div>

      <div class="sheet-corner is-left"><slot name="bottom-left" /></div>

      <div class="sheet-corner is-right" :class="{ 'is-stood-back': photoLifted }">
        <slot name="bottom-right" />
      </div>

      <!-- The bottom edge, between the corners: for what belongs to the sheet
           as a whole rather than to any one thing standing on it. -->
      <div class="sheet-corner is-centre" :class="{ 'is-stood-back': photoLifted }">
        <slot name="footer" />
      </div>

      <!-- Under the board, the way a photo editor keeps its tools under the
           picture. What happens to the game stands at the right end of the row. -->
      <div v-if="slots.toolbar || slots.step" class="sheet-tools">
        <div v-if="slots.toolbar" class="sheet-toolbar"><slot name="toolbar" /></div>
        <div v-if="slots.step" class="sheet-step"><slot name="step" /></div>
      </div>

      <template #overlay>
        <slot name="overlay" />
      </template>
    </SimpleDialog>
  </Teleport>
</template>

<script lang="ts" setup>
import SimpleDialog from '@/views/components/gam-dialog/SimpleDialog.vue';
import { computed, useSlots } from 'vue';

export interface HandshakeSheetProps {
  /**
   * A handshake with a photo on it shows the photo where the board would be,
   * rather than over it: the picture is carried at a third of its strength so
   * whatever stands on the sheet can be read over it, and a board left
   * underneath would come through and muddy it.
   */
  photo?: string;
  /** Held at full strength, for pressing a photo to see it properly. */
  photoLifted?: boolean;
  /**
   * The top slot is a heading for what follows rather than one of three things
   * weighed against each other, so it stands on the top edge — the far side of
   * the same margin the footer stands on — and what it introduces takes the
   * middle of everything left between them.
   */
  leadAtTop?: boolean;
  /**
   * Stands the top slot on the top edge while the rows stay even, for a sheet
   * whose centrepiece has to hold the middle of the board exactly: a round one
   * reads as off-centre the moment it does not, and what is above it is better
   * moved up than left to crowd it.
   */
  topAtEdge?: boolean;
  /**
   * The centrepiece keeps the middle line even when something sits under it.
   * Otherwise that lower line weighs the rows and the centrepiece drops.
   */
  keepCentre?: boolean;
  /**
   * Off when the sheet comes up inside something that already washes the page
   * out — a vue-final-modal — so the page is not dimmed twice over.
   */
  dimmed?: boolean;
  /**
   * The cover slot takes the board instead of the usual rows. A later step of
   * the same sheet — sharing a code, typing one back — stands there.
   */
  cover?: boolean;
  /** The step slot has a button in it, so the toolbar keeps the left and the step the right. */
  stepShown?: boolean;
}

const props = withDefaults(defineProps<HandshakeSheetProps>(), {
  photo: undefined,
  dimmed: true,
  cover: false,
  stepShown: false,
});

const emit = defineEmits<{ close: [] }>();

const slots = useSlots();

const contentClass = computed(() => ({
  'is-stood-back': props.photoLifted,
  'is-cover': props.cover,
  'has-footer': !props.cover && !!slots.footer,
  'has-bottom': !props.cover && !!slots.bottom,
  'is-lead-at-top': !props.cover && props.leadAtTop,
  'is-centre-kept': !props.cover && props.keepCentre,
  'is-top-at-edge': !props.cover && props.topAtEdge,
}));
</script>

<style lang="scss" scoped>
/*
 * Dialogs have their native backdrop turned off app-wide, because the ones that
 * come up inside a vue-final-modal already sit on that library's overlay and
 * would otherwise be dimmed twice. A sheet can stand on its own, so it takes
 * the same wash the hard way.
 */
.sheet-content.is-cover {
  display: block;
}

.handshake-sheet.is-dimmed::backdrop {
  display: block;
  background: var(--color-modal-overlay);
}

/* A modal dialog is held short of the screen by the browser and clips at that
   edge, which would cut the toolbar row where it runs past the board. */
.handshake-sheet.simple-dialog {
  max-width: 100%;
}

.handshake-sheet :deep(.simple-dialog-modal) {
  width: 100%;
  align-items: center;
  justify-content: center;
}

/*
 * A board is square, so the sheet is too: it takes whichever of the screen's
 * two dimensions runs out first, less the margin that keeps it clear of the
 * edge. The class is chained and the sheet named in the selector because
 * SimpleDialog sizes this element three selectors deep in its own scope, and a
 * shorter selector than that loses to it and leaves the sheet oblong. Its
 * mobile variant is named for the same reason: it re-pads the sheet from a
 * selector that would otherwise tie with this one and win on order.
 *
 * Nothing here may clip: the halo has to carry past the edge, so the board and
 * the photo round their own corners instead.
 */
.handshake-sheet.simple-dialog :deep(.simple-dialog-modal .simple-dialog-content),
.handshake-sheet.simple-dialog :deep(.simple-dialog-modal.mobile .simple-dialog-content) {
  width: min(88vw, 100dvh - var(--spacing-xxl), 420px);
  aspect-ratio: 1;
  padding: var(--spacing-h);
  justify-content: center;
  min-height: 0;
  overflow: visible;
  // The Walk board's hairline ring and halo, scaled up for a board this size
  // and kept over the drop shadow that lifts a dialog off the page it covers.
  box-shadow:
    0 0 0 0.5px rgba(255, 255, 255, 0.08),
    0 0 48px 10px oklch(0.78 0.24 var(--hue) / 0.08),
    0 8px 32px rgba(0, 0, 0, 0.5);
}

/*
 * The toolbar hangs under the board, so the board gives up its height on a short
 * screen, and the two are centred together rather than the board alone.
 */
.handshake-sheet {
  --sheet-toolbar-room: calc(44px + var(--spacing-s) * 2 + var(--spacing-l));
}

.handshake-sheet.has-toolbar.simple-dialog :deep(.simple-dialog-modal .simple-dialog-content),
.handshake-sheet.has-toolbar.simple-dialog :deep(.simple-dialog-modal.mobile .simple-dialog-content),
.handshake-sheet.has-step.simple-dialog :deep(.simple-dialog-modal .simple-dialog-content),
.handshake-sheet.has-step.simple-dialog :deep(.simple-dialog-modal.mobile .simple-dialog-content) {
  width: min(88vw, 100dvh - var(--spacing-xxl) - var(--sheet-toolbar-room), 420px);
  margin-bottom: var(--sheet-toolbar-room);
}

/* As wide as the board, with the toolbar on its left. A step on the right may
   take the margins beside the board, and stops a fixed inset short of the screen. */
.sheet-tools {
  position: absolute;
  top: calc(100% + var(--spacing-l));
  left: 50%;
  z-index: 6;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: var(--spacing-md);
  width: 100%;
  transform: translateX(-50%);
  pointer-events: none;

  > * {
    pointer-events: auto;
  }
}

.handshake-sheet.has-step-shown .sheet-tools {
  justify-content: space-between;
  width: min(calc(100dvw - var(--spacing-l) * 2), 420px);
}

.sheet-step {
  display: flex;
  flex: none;
  align-items: center;
}

/* One surface under the row, so whatever the sheet opened over never shows between the buttons. */
.sheet-toolbar {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-s);
  border-radius: 999px;
  background: var(--color-dark-900);
  box-shadow:
    inset 0 0 0 1px var(--color-white-10),
    0 8px 24px var(--color-dark-50);

  &:empty {
    display: none;
  }
}

/* A phone, with the game's button beside the toolbar: the toolbar tightens so both fit. */
@media (max-width: 430px) {
  .handshake-sheet.has-step-shown .sheet-toolbar {
    gap: var(--spacing-xs);
    padding: var(--spacing-xs);
  }
}

/*
 * Eight squares across however wide it is drawn, in two greys close enough
 * together to stay a texture rather than a picture of a board.
 *
 * One tile of the gradient is a two-by-two of squares, so a quarter of the
 * sheet per tile is what makes eight across and sixty-four in all. The cone
 * turns from noon, so its second quarter is the one at the bottom left of every
 * tile, and the dark square a real board starts on down there is what decides
 * which colour goes first.
 */
.sheet-board {
  position: absolute;
  inset: 0;
  border-radius: var(--radius-large);
  background-image: repeating-conic-gradient(var(--color-dark-700) 0% 25%, var(--color-dark-600) 0% 50%);
  background-size: 25% 25%;
  pointer-events: none;
}

.sheet-photo {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: var(--radius-large);
  opacity: 0.33;
  transition: opacity var(--transition);
  pointer-events: none;
}

.sheet-photo.is-lifted {
  opacity: 1;
}

/*
 * Whatever the sheet is carrying sits on the middle of the board, which puts it
 * on the line between the fourth and fifth rank however tall the rest is. Equal
 * rows above and below hold it there.
 */
.sheet-content {
  position: relative;
  z-index: 5;
  display: grid;
  // A square is a tighter frame than a portrait card, so the rows are allowed
  // to shrink under their content and the sheet keeps whatever spills inside
  // its own edges rather than letting it run out onto the page.
  grid-template-rows: minmax(0, 1fr) auto minmax(0, 1fr);
  justify-items: center;
  align-items: center;
  width: 100%;
  height: 100%;
  overflow: hidden;
  text-align: center;
  // What a sheet carries is sized against the board rather than the screen, so
  // a centrepiece keeps its share of the square at any size.
  container-type: size;
  container-name: handshake-sheet;

  // It holds the focus on the way in without having been aimed at, so it is not
  // ringed for it.
  &:focus {
    outline: none;
  }
}

/*
 * A heading is not one of the three weights. It stands on the top edge — the far
 * side of the same margin the footer stands on — while the rows stay even, so
 * the centrepiece keeps the middle of the board and what is above and below it
 * hangs off that line rather than off the heading. The rest of the top row is
 * pushed down against the centrepiece to meet it, and the bottom row holds its
 * content up under it, which is what leaves the footer its strip.
 */
.sheet-content.is-lead-at-top .sheet-top {
  align-self: stretch;
  justify-content: space-between;
}

.sheet-content.is-lead-at-top .sheet-bottom {
  align-self: stretch;
  justify-content: flex-start;
}

/*
 * A heading, a centrepiece and a line under it, read in that order, want to
 * stand on the board rather than hang off its top edge with the air left under
 * them. So the rows are weighed apart: the top row takes the larger share — it
 * is the one with the heading to place — and everything comes down off the
 * middle line by the difference. The share is a ratio rather than a margin
 * because a full board has nothing to give: the rows then come back to even of
 * their own accord instead of pushing what they carry off the sheet.
 *
 * A sheet with nothing under its centrepiece has nothing to weigh, and keeps
 * the middle line.
 */
.sheet-content.is-lead-at-top.has-bottom:not(.is-centre-kept) {
  grid-template-rows: minmax(0, 1.2fr) auto minmax(0, 1fr);
}

.sheet-content.is-lead-at-top.has-footer {
  padding-bottom: 0;
}

/*
 * The rows stay even — so the centrepiece keeps the middle line — and each of
 * them hands its content the far end of whatever room it has instead of the
 * middle of it. That is the whole of the difference from a heading at the top:
 * nothing is weighed, the centrepiece does not move, and what is above and
 * below it stands as far off it as the rows allow.
 *
 * Both ends, not just the top: content held to one edge and floating at the
 * other is the asymmetry the eye picks up first, since the edges of the sheet
 * are what it has to measure against. The air the two rows have left over is
 * not equal — that depends on what each is carrying — but it all falls either
 * side of the centrepiece, where it reads as the room around it rather than
 * as a margin that changes from top to bottom.
 */
.sheet-content.is-top-at-edge .sheet-top {
  align-self: stretch;
  justify-content: flex-start;
}

.sheet-content.is-top-at-edge .sheet-bottom {
  align-self: stretch;
  justify-content: flex-end;
}

/*
 * A footer stands on the bottom edge rather than in the rows, so the rows give
 * up the strip it stands on — and a good deal more than it takes, so what the
 * rows end on stands clear of it rather than reading as part of it.
 */
.sheet-content.has-footer {
  padding-bottom: calc(var(--spacing-xxl) + var(--spacing-l));
}

/* Padding only the bottom would lift the centrepiece, so the row under it holds its content up instead. */
.sheet-content.has-footer.is-centre-kept {
  padding-bottom: 0;

  .sheet-bottom {
    align-self: stretch;
    justify-content: flex-start;
  }
}

/*
 * The rows either side of the centrepiece hold their content in the middle of
 * whatever room they are left, so what is above it and what is below it stand
 * off it by the same distance rather than one hugging the edge of the board
 * while the other crowds the middle.
 */
.sheet-top {
  grid-row: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-md);
  width: 100%;
}

.sheet-middle {
  grid-row: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.sheet-bottom {
  grid-row: 3;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-md);
  width: 100%;
}

/*
 * The corners hold what belongs to the sheet rather than to what it says — the
 * photo on it and what can be done to it — so they sit outside the middle
 * column and stay put whatever height that column comes to.
 */
.sheet-corner {
  position: absolute;
  bottom: var(--spacing-h);
  z-index: 6;
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
}

.sheet-corner.is-top-left {
  top: var(--spacing-h);
  left: var(--spacing-h);
  bottom: auto;
  justify-content: flex-start;
}

.sheet-corner.is-top-right {
  top: var(--spacing-h);
  right: var(--spacing-h);
  bottom: auto;
  justify-content: flex-end;
}

.sheet-corner.is-left {
  left: var(--spacing-h);
  justify-content: flex-start;
}

.sheet-corner.is-right {
  right: var(--spacing-h);
  justify-content: flex-end;
}

.sheet-corner.is-centre {
  left: 50%;
  transform: translateX(-50%);
  justify-content: center;
}

/*
 * Holding the photo down is asking to see the photo, so everything standing on
 * it steps aside for as long as it is held.
 */
.sheet-content,
.sheet-corner {
  transition: opacity var(--transition);
}

.sheet-content.is-stood-back,
.sheet-corner.is-stood-back {
  opacity: 0;
}
</style>
